import { ChangeEvent, useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import FileApi from "../api/file";
import NewRequestResendCodeApi from "../api/newRequest-resendCode";
import LineApi from "../api/line";

import AuthContext from "../components/auth-context";
import useAppNavigate from "../components/router/use-app-navigate";
import Form from "../components/inputs/form";
import Textbox from "../components/inputs/textbox";
import Textarea from "../components/inputs/textarea";
import VerifyNumber from "../components/signup/verify-number";

import { localizeNumbers, unLocalizeNumbers } from "../functions";

interface RequestForm {
  phone: string;
  subCenterId: string;
  ownershipType: string;
  agentCode: string;
  installAddress: string;
}

interface SubCenter {
  id: number;
  title: string;
}

interface PhoneInfo {
  city?: string;
  center?: string;
  subCentersList?: SubCenter[];
}

function Request() {
  const [phone, setPhone] = useState("");
  const [subCenterId, setSubCenterId] = useState<string>("0");
  const [ownershipType, setOwnershipType] = useState("");
  const [agentCode, setAgentCode] = useState("");
  const [installAddress, setInstallAddress] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [toggle, setToggle] = useState(true);

  const [phoneInfo, setPhoneInfo] = useState<PhoneInfo | null>(null);
  const [apiResponse, setApiResponse] = useState<"success" | "error" | null>(
    null,
  );
  const [phoneError, setPhoneError] = useState<string>("");

  const { register, handleSubmit } = useForm<RequestForm>();
  const navigate = useAppNavigate();
  const { t } = useTranslation(undefined, { keyPrefix: "Request.Adsl" });
  const context = useContext(AuthContext);
  const { customerInfo } = context;

  const renderError = (err: any): string => {
    if (!err) return "";
    if (typeof err === "string") return err;
    if ("errorMessage" in err && typeof err.errorMessage === "string")
      return err.errorMessage;
    return JSON.stringify(err);
  };

  const onSubmit = async (data: RequestForm) => {
    setPhone(data.phone);
    setSubCenterId(data.subCenterId);
    setOwnershipType(data.ownershipType);
    setAgentCode(data.agentCode);
    setInstallAddress(data.installAddress);

    const response = await new NewRequestResendCodeApi(context).get("ADSL");
    if (!response.errorMessage) setIsVerifying(true);

    return response.errorMessage;
  };

  const onVerifyNumberSubmit = async () => {
    try {
      const response = await new FileApi(context).post(
        0,
        phone,
        subCenterId,
        agentCode,
        ownershipType,
        installAddress,
        "",
      );

      if (response?.id) {
        context.setActiveFileId(response.id);
        navigate("/");
      } else {
        alert(renderError(response?.errorMessage || "خطایی رخ داده است."));
      }
    } catch (err) {
      alert("خطا در ارسال اطلاعات");
      console.error(err);
    }
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = unLocalizeNumbers(e.target.value);
    setPhone(value);

    if (value.length === 11) {
      new LineApi(context)
        .get(value)
        .then((data) => {
          if (data.errorMessage) {
            setApiResponse("error");
            setPhoneError(renderError(data.errorMessage));
          } else {
            setApiResponse("success");
            setPhoneInfo({
              city: String(data.city || "-"),
              center: String(data.center || "-"),
              subCentersList: data.subCentersList?.map((sc: any) => ({
                id: sc.id,
                title: String(sc.title),
              })),
            });
            setSubCenterId(data.subCentersList?.[0]?.id.toString() || "0");
          }
        })
        .catch(() => {
          setApiResponse("error");
          setPhoneError("خطا در ارتباط با سرور");
        });
    } else {
      setPhoneInfo(null);
      setApiResponse(null);
      setPhoneError("");
      setSubCenterId("0");
    }
  };

  const displayName = `${customerInfo?.firstName || ""} ${
    customerInfo?.lastName || ""
  }`.trim();
  const displayNationalCode = customerInfo?.nationalCode
    ? localizeNumbers(customerInfo.nationalCode)
    : "";
  const displayUsername = customerInfo?.cellPhone
    ? localizeNumbers(customerInfo.cellPhone)
    : "";

  return (
    <>
      {!isVerifying ? (
        <Form
          submit={onSubmit}
          handleSubmit={handleSubmit}
          className="space-y-3"
        >
          <h3 className="lead-title pt-2">{t("Title")}</h3>

          <Textbox
            {...register("phone", { required: true })}
            label="تلفن"
            helpNote="شماره تلفن بصورت کامل به همراه کد شهر. نمونه: ۰۲۱۸۴۲۱۱۰۰۰"
            onChange={handlePhoneChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-400 text-sm"
          />

          {apiResponse === "error" && phoneError && (
            <p className="text-red-500 text-xs">{phoneError}</p>
          )}

          {apiResponse === "success" && phoneInfo && (
            <div className="form-group flex flex-row flex-wrap justify-between xs:w-full sm:w-full lg:w-3/4 text-right mt-6 relative">
              <div className="xs:w-1/2">
                <h5 className="pb-5">شهر :</h5>
                <span className="block p-1 bg-gray-100 rounded">
                  {phoneInfo?.city}
                </span>
              </div>

              <div className="xs:w-1/2">
                <label className="pr-4 font-medium">
                  {`مرکز مخابراتی ${phoneInfo?.center}`} :
                </label>
                {phoneInfo?.subCentersList?.length !== 0 && (
                  <select
                    {...register("subCenterId")}
                    value={subCenterId}
                    onChange={(e) => setSubCenterId(e.target.value)}
                    className="mt-2 xs:w-full p-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-400 text-sm"
                  >
                    <option value="0">هیچکدام</option>
                    {phoneInfo?.subCentersList?.map((sc) => (
                      <option key={sc.id} value={sc.id}>
                        {sc.title}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          )}

          <div className="form-group row">
            <div className="col-sm-3 col-form-label text-sm-right pr-0">
              <label htmlFor="id-form-field-3" className="mb-0">
                <span className="block font-medium">نوع مالکیت</span>
              </label>
            </div>
            <div className="col-sm-9">
              <select
                {...register("ownershipType")}
                value={ownershipType}
                onChange={(e) => setOwnershipType(e.target.value)}
                className="mt-1 w-full p-1 border border-gray-300 rounded focus:ring-1 focus:ring-blue-400 text-sm"
              >
                <option value="">نوع مالکیت را انتخاب کنید</option>
                <option value="مالک">مالک</option>
                <option value="مستاجر">مستاجر</option>
              </select>
            </div>
          </div>

          <Textbox
            {...register("agentCode")}
            label="کد نمایندگی"
            helpNote="در صورت معرفی توسط نماینده"
            onChange={(e) => setAgentCode(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-400 text-sm"
          />
          <Textarea
            {...register("installAddress")}
            label="آدرس نصب"
            helpNote="در صورت نیاز به نصب توسط کارشناس"
            onChange={(e) => setInstallAddress(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-1 focus:ring-blue-400 text-sm"
          />
          <div className="form-section rtl flex flex-grow xs:flex-col sm:flex-row  gap-4 justify-between">
            <div className="rule-section flex algin-center items-center gap-2">
              <input
                id="role-checkbox"
                type="checkbox"
                tabIndex={19}
                onClick={() => setToggle(!toggle)}
              />
              <label className="role-checkbox-label" htmlFor="role-checkbox">
                <p className="w-50">
                  {t("I had read and accept the contract", {
                    name: `${customerInfo?.firstName ?? ""} ${
                      customerInfo?.lastName ?? ""
                    }`,
                    nationalCode: localizeNumbers(
                      customerInfo?.nationalCode || "",
                    ),
                    service: "ADSL",
                    username: localizeNumbers(customerInfo?.cellPhone || ""),
                  })}
                  <small>
                    <Link
                      to="https://internet.sepanta.com/docs/CustomerContract.pdf"
                      className="btn-show-role font-bold"
                    >
                      ({t("Read contract")})
                    </Link>
                  </small>
                </p>
              </label>
            </div>
          </div>

          <button disabled={toggle} className="signup-btn p-5 flex">
            {t("Accept & Continue")}
            <span className="sepanta-icon-left-long-arrow ml-1"></span>
          </button>
        </Form>
      ) : (
        <VerifyNumber
          username={
            customerInfo?.company?.nationalIdentity != null
              ? customerInfo.company.nationalIdentity.toString()
              : customerInfo?.cellPhone || ""
          }
          cellPhone={String(customerInfo?.cellPhone ?? "")}
          onSubmit={onVerifyNumberSubmit}
          handleAlert={(msg) =>
            alert(typeof msg === "string" ? msg : JSON.stringify(msg))
          }
          alert={false}
          serviceType="ADSL"
        />
      )}
    </>
  );
}

export default Request;
